import React, { useState, useRef, useEffect } from 'react';
import { useFeatureFlags } from '../config/featureFlags';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { refreshAuthentication, checkTokenExpiry } from '../store/slices/authSlice';

const TokenRefreshTest: React.FC = () => {
  const { flags } = useFeatureFlags();
  const dispatch = useAppDispatch();
  const auth = useAppSelector(state => state.auth);
  const [isLoading, setIsLoading] = useState(false);
  const [lastAction, setLastAction] = useState<string>('');
  
  // Drag functionality
  const [position, setPosition] = useState(() => {
    const saved = localStorage.getItem('tokenRefreshTestPosition');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.warn('Failed to parse saved position:', e);
      }
    }
    return { x: 20, y: 20 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const panelRef = useRef<HTMLDivElement>(null);

  // Save position to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('tokenRefreshTestPosition', JSON.stringify(position));
  }, [position]);

  // Mouse event handlers for dragging
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target === panelRef.current || (panelRef.current?.contains(e.target as Node))) {
      setIsDragging(true);
      const rect = panelRef.current?.getBoundingClientRect();
      if (rect) {
        setDragOffset({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top
        });
      }
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      // Keep panel within viewport bounds
      const maxX = window.innerWidth - 300; // panel width
      const maxY = window.innerHeight - 200; // approximate panel height
      
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  // Only render if the feature flag is enabled and user is authenticated
  if (!flags.enableTokenRefreshTest || !auth.isAuthenticated) {
    return null;
  }

  const handleRefreshToken = async () => {
    setIsLoading(true);
    setLastAction('Refreshing token...');
    
    try {
      const result = await dispatch(refreshAuthentication());
      if (result.meta.requestStatus === 'fulfilled') {
        setLastAction('Token refreshed successfully!');
      } else {
        setLastAction('Token refresh failed!');
      }
    } catch (error) {
      setLastAction('Token refresh error!');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckExpiry = async () => {
    setIsLoading(true);
    setLastAction('Checking token expiry...');
    
    try {
      const result = await dispatch(checkTokenExpiry());
      if (result.meta.requestStatus === 'fulfilled') {
        const payload = result.payload as any;
        if (payload.expired) {
          setLastAction('Token is expired!');
        } else if (payload.skipped) {
          setLastAction('Token check skipped (too recent)');
        } else {
          setLastAction('Token is valid!');
        }
      } else {
        setLastAction('Token check failed!');
      }
    } catch (error) {
      setLastAction('Token check error!');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      ref={panelRef}
      style={{
        position: 'fixed',
        top: position.y,
        left: position.x,
        width: '300px',
        backgroundColor: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        zIndex: 1000,
        fontSize: '14px',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none'
      }}
      onMouseDown={handleMouseDown}
    >
      <h4 style={{ 
        margin: '0 0 12px 0', 
        color: '#495057',
        cursor: 'grab',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <span>🔧</span>
        <span>Token Refresh Test</span>
        <span style={{ fontSize: '12px', color: '#6c757d', marginLeft: 'auto' }}>
          (Drag to move)
        </span>
        <button
          onClick={() => {
            setPosition({ x: 20, y: 20 });
            localStorage.removeItem('tokenRefreshTestPosition');
          }}
          style={{
            background: 'none',
            border: 'none',
            color: '#6c757d',
            cursor: 'pointer',
            fontSize: '12px',
            padding: '2px 6px',
            borderRadius: '3px',
            marginLeft: '8px'
          }}
          title="Reset position"
        >
          ↺
        </button>
      </h4>
      
      <div style={{ marginBottom: '12px' }}>
        <strong>Auth Type:</strong> {auth.type || 'None'}
      </div>
      
      <div style={{ marginBottom: '12px' }}>
        <strong>Token Expiry:</strong> {auth.tokenExpiry ? new Date(auth.tokenExpiry).toLocaleString() : 'None'}
      </div>
      
      <div style={{ marginBottom: '16px' }}>
        <strong>Last Action:</strong> {lastAction}
      </div>
      
      <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
        <button
          onClick={handleRefreshToken}
          disabled={isLoading || !auth.isAuthenticated}
          style={{
            padding: '8px 12px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.6 : 1
          }}
        >
          {isLoading ? 'Refreshing...' : 'Refresh Token'}
        </button>
        
        <button
          onClick={handleCheckExpiry}
          disabled={isLoading || !auth.isAuthenticated}
          style={{
            padding: '8px 12px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.6 : 1
          }}
        >
          {isLoading ? 'Checking...' : 'Check Expiry'}
        </button>
      </div>
      
      <div style={{ 
        marginTop: '12px', 
        padding: '8px', 
        backgroundColor: '#e9ecef', 
        borderRadius: '4px',
        fontSize: '12px'
      }}>
        <strong>Debug Info:</strong>
        <div>Authenticated: {auth.isAuthenticated ? 'Yes' : 'No'}</div>
        <div>Last Activity: {new Date(auth.lastActivity).toLocaleTimeString()}</div>
        <div>Last Check: {new Date(auth.lastTokenCheck).toLocaleTimeString()}</div>
      </div>
    </div>
  );
};

export default TokenRefreshTest; 